import React, { useEffect, useMemo, useState } from "react";
import { X, Star, Loader2, RefreshCw, Subtitles, Share2, Tv2 } from "lucide-react";
import TvAvailableBadge, { TvCoverageStrip } from "@/components/TvAvailableBadge";
import { useRegion } from "@/lib/region";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import Lightbox from "@/components/Lightbox";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import TorrentList from "@/components/TorrentList";
import ListToggleButtons from "@/components/ListToggleButtons";
import CommentsChatTabs from "@/components/CommentsChatTabs";
import { SourceBadge, LanguageBadge } from "@/components/TorrentBadges";

const QUALITIES = ["ALL", "HD", "4K", "SD"];
const CODECS = ["ALL", "HEVC", "H.264", "AV1", "XviD"];
const LANGUAGES = ["ALL", "Multi", "English", "Spanish", "French", "German", "Italian", "Russian", "Japanese", "Korean", "Hindi", "Chinese", "Portuguese", "Turkish", "Arabic", "Polish", "Dutch", "Swedish"];

function classifyTvTorrent(t) {
  if (t.override_category) return t.override_category;
  const n = (t.name || "").toLowerCase();
  if (/s\d{1,2}\s?e\d{1,3}/i.test(n)) return "episode";
  if (/(complete|all\s+seasons|s\d{1,2}\s?-\s?s\d{1,2})/i.test(n)) return "series";
  if (/(season\s*\d|\bs\d{1,2}\b)/i.test(n)) return "season";
  return "episode";
}

export default function DetailModal({ item, onClose }) {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const region = useRegion();
  const { user } = useAuth();
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [torrents, setTorrents] = useState(null);
  const [collectionTorrents, setCollectionTorrents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTor, setLoadingTor] = useState(false);
  const [quality, setQuality] = useState("ALL");
  const [codecFilter, setCodecFilter] = useState(() => {
    try {
      return localStorage.getItem("torhub:codecFilter") || "ALL";
    } catch {
      return "ALL";
    }
  });
  const [languageFilter, setLanguageFilter] = useState(() => {
    try {
      return localStorage.getItem("torhub:langFilter") || "ALL";
    } catch {
      return "ALL";
    }
  });

  // Persist the user's language preference so it sticks across modal opens
  useEffect(() => {
    try { localStorage.setItem("torhub:langFilter", languageFilter); } catch { /* no-op */ }
  }, [languageFilter]);

  // Persist the user's codec preference (HEVC / H.264 / AV1 / XviD) too
  useEffect(() => {
    try { localStorage.setItem("torhub:codecFilter", codecFilter); } catch { /* no-op */ }
  }, [codecFilter]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [season, setSeason] = useState("");
  const [episode, setEpisode] = useState("");
  const [episodes, setEpisodes] = useState([]);
  const [subtitles, setSubtitles] = useState(null);
  const [subsBusy, setSubsBusy] = useState(false);

  // Lock body scroll
  useEffect(() => {
    if (item) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [item]);

  // Inject BreadcrumbList JSON-LD while modal is open (Home › Movies/TV › Title)
  useEffect(() => {
    if (!item || !details) return;
    const origin = window.location.origin;
    const mt = details.media_type || item.media_type;
    const titleText = details.title || item.title || item.name || "";
    const section = mt === "tv" ? "TV Shows" : "Movies";
    const ld = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: section, item: `${origin}/?cat=popular&type=${mt}` },
        { "@type": "ListItem", position: 3, name: titleText, item: `${origin}/title/${mt}/${item.id}` },
      ],
    };
    const SCRIPT_ID = "ld-json-breadcrumbs";
    const old = document.getElementById(SCRIPT_ID);
    if (old) old.remove();
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [item, details]);

  useEffect(() => {
    if (!item) return;
    setDetails(null);
    setTorrents(null);
    setCollectionTorrents(null);
    setQuality("ALL");
    // codecFilter intentionally NOT reset — it persists as a user preference
    // languageFilter intentionally NOT reset — it persists across titles
    setVerifiedOnly(false);
    setSourceFilter("ALL");
    setSeason("");
    setEpisode("");
    setEpisodes([]);
    setSubtitles(null);

    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/details/${item.media_type}/${item.id}`, { params: { region } });
        if (!cancel) setDetails(data);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [item, region]);

  // Load season episodes when season picked (TV)
  useEffect(() => {
    if (!details || details.media_type !== "tv" || !season) {
      setEpisodes([]);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/tv/${details.id}/season/${season}`);
        if (!cancel) setEpisodes(data.episodes || []);
      } catch { /* season fetch failed */ }
    })();
    return () => { cancel = true; };
  }, [details, season]);

  // Fetch torrents whenever season/episode change (after details loaded)
  useEffect(() => {
    if (!details) return;
    let cancel = false;
    (async () => {
      try {
        setLoadingTor(true);
        const params = {
          tmdb_id: details.id,
          title: details.title,
          media_type: details.media_type,
          year: details.year || undefined,
          imdb_id: details.imdb_id || undefined,
          season: season || undefined,
          episode: episode || undefined,
        };
        const { data } = await api.get(`/torrents`, { params });
        if (!cancel) setTorrents(data.results || []);
      } catch {
        if (!cancel) setTorrents([]);
      } finally {
        if (!cancel) setLoadingTor(false);
      }
    })();
    return () => { cancel = true; };
  }, [details, season, episode]);

  // Fetch collection (franchise) torrents when the movie belongs to a TMDB collection
  useEffect(() => {
    if (!details || details.media_type !== "movie" || !details.collection?.name) {
      setCollectionTorrents(null);
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/torrents/collection`, {
          params: { name: details.collection.name },
        });
        if (!cancel) setCollectionTorrents(data.results || []);
      } catch {
        if (!cancel) setCollectionTorrents([]);
      }
    })();
    return () => { cancel = true; };
  }, [details]);

  const filtered = useMemo(() => {
    if (!torrents) return [];
    return torrents.filter((t) => {
      if (quality !== "ALL" && (t.quality || "SD") !== quality) return false;
      if (codecFilter !== "ALL" && (t.codec || "Other") !== codecFilter) return false;
      if (languageFilter !== "ALL" && (t.language || "Unknown") !== languageFilter) return false;
      if (verifiedOnly && (t.seeders || 0) < 10) return false;
      if (sourceFilter !== "ALL" && (t.source || "") !== sourceFilter) return false;
      return true;
    });
  }, [torrents, quality, codecFilter, languageFilter, verifiedOnly, sourceFilter]);

  const sourceCounts = useMemo(() => {
    if (!torrents) return {};
    const out = {};
    for (const t of torrents) {
      const s = t.source || "Unknown";
      out[s] = (out[s] || 0) + 1;
    }
    return out;
  }, [torrents]);

  const languageCounts = useMemo(() => {
    if (!torrents) return {};
    const out = {};
    for (const t of torrents) {
      const l = t.language || "Unknown";
      out[l] = (out[l] || 0) + 1;
    }
    return out;
  }, [torrents]);

  // If the saved language preference has 0 hits for this title, fall back to ALL
  // so the user isn't staring at an empty filtered list.
  useEffect(() => {
    if (!torrents || languageFilter === "ALL") return;
    if ((languageCounts[languageFilter] || 0) === 0) {
      setLanguageFilter("ALL");
    }
  }, [torrents, languageCounts, languageFilter]);

  // Same fallback for codec — if no torrents match the saved codec, reset to ALL
  useEffect(() => {
    if (!torrents || codecFilter === "ALL") return;
    const hits = torrents.filter((t) => (t.codec || "Other") === codecFilter).length;
    if (hits === 0) setCodecFilter("ALL");
  }, [torrents, codecFilter]);

  const fetchSubtitles = async () => {
    if (!details) return;
    setSubsBusy(true);
    try {
      const params = {
        imdb_id: details.imdb_id || undefined,
        tmdb_id: details.imdb_id ? undefined : details.id,
        query: details.imdb_id ? undefined : details.title,
        languages: "en",
        season: season || undefined,
        episode: episode || undefined,
      };
      const { data } = await api.get("/subtitles", { params });
      setSubtitles(data);
    } catch (e) {
      setSubtitles({ error: e.response?.data?.detail || "Subtitles unavailable" });
    } finally {
      setSubsBusy(false);
    }
  };

  const reset = () => { setSeason(""); setEpisode(""); };

  const refreshTorrents = async () => {
    if (!details) return;
    setLoadingTor(true);
    try {
      const { data } = await api.get(`/torrents`, {
        params: {
          tmdb_id: details.id, title: details.title, media_type: details.media_type,
          year: details.year || undefined, imdb_id: details.imdb_id || undefined,
          season: season || undefined, episode: episode || undefined, nocache: true,
        },
      });
      setTorrents(data.results || []);
    } finally {
      setLoadingTor(false);
    }
  };

  if (!item) return null;

  const shareUrl = `${window.location.origin}/api/share/${item.media_type}/${item.id}`;
  const shareTitle = details?.title || item.title || item.name || "Check this out on TorHub";

  const onShare = async () => {
    const data = { title: shareTitle, text: `${shareTitle} — TorHub`, url: shareUrl };
    if (navigator.share) {
      try {
        await navigator.share(data);
        return;
      } catch {
        // user cancelled or share failed — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — long-press the URL bar instead");
    }
  };

  return (
    <div
      data-testid="detail-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-stretch md:items-center justify-center md:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full md:max-w-5xl md:h-[92vh] bg-[var(--tt-surface)] md:rounded-2xl border border-[var(--tt-border)] overflow-hidden flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
          <button
            onClick={onShare}
            data-testid="detail-modal-share"
            title="Share this title"
            className="h-9 px-3 rounded-full bg-[var(--tt-primary)]/15 hover:bg-[var(--tt-primary)]/25 border border-[var(--tt-primary)]/40 text-[var(--tt-primary)] flex items-center gap-1.5 text-xs font-semibold transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          <button
            onClick={onClose}
            data-testid="detail-modal-close"
            className="w-9 h-9 rounded-full bg-black/60 hover:bg-black border border-[var(--tt-border)] flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto">
          <div className="relative aspect-[16/9] md:aspect-[21/9] w-full bg-[#0c0c0c]">
            {(details?.backdrop || item.backdrop) ? (
              <img
                src={details?.backdrop || item.backdrop}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : loading ? (
              <div className="absolute inset-0 tt-skeleton" data-testid="detail-modal-backdrop-skeleton" />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--tt-surface)] via-[var(--tt-surface)]/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              {details?.logo ? (
                <img
                  src={details.logo}
                  alt={details?.title || item.title}
                  data-testid="detail-modal-title-logo"
                  className="max-h-20 md:max-h-28 w-auto max-w-[80%] object-contain drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]"
                />
              ) : (
                <h1
                  data-testid="detail-modal-title-text"
                  className="font-display text-3xl md:text-4xl font-extrabold tracking-tight"
                >
                  {details?.title || item.title}
                </h1>
              )}
              <div className="flex items-center gap-3 mt-3 text-sm">
                <span className="flex items-center gap-1 text-[#FFD700] font-semibold">
                  <Star className="w-4 h-4 fill-[#FFD700]" strokeWidth={0} />
                  {(details?.rating ?? item.rating ?? 0).toFixed(1)}
                </span>
                <span className="text-[var(--tt-text-muted)]">{details?.year || item.year}</span>
                {details?.runtime && (
                  <span className="text-[var(--tt-text-muted)]">{details.runtime}m</span>
                )}
                {details?.genres?.slice(0, 3).map((g) => (
                  <span key={g} className="text-xs bg-white/10 px-2 py-0.5 rounded-full">{g}</span>
                ))}
                <TvAvailableBadge
                  tmdbId={item.id}
                  mediaType={details?.media_type || item.media_type}
                />
              </div>
              <TvCoverageStrip
                tmdbId={item.id}
                mediaType={details?.media_type || item.media_type}
                className="mt-3"
              />

              {/* Streaming on — TMDB watch providers (US flatrate) */}
              {details?.watch_providers?.length > 0 && (
                <div
                  className="flex items-center gap-2 mt-3 flex-wrap"
                  data-testid="watch-providers-row"
                >
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-text-muted)]">
                    <Tv2 className="w-3 h-3" /> Streaming on
                  </span>
                  {details.watch_providers.slice(0, 6).map((p) => {
                    const href = details.watch_link;
                    const inner = (
                      <span
                        className="inline-flex items-center gap-1.5 bg-white/8 hover:bg-white/15 border border-white/10 hover:border-white/30 text-[11px] font-semibold px-1.5 py-1 rounded-md transition-colors"
                        title={p.name}
                      >
                        {p.logo && (
                          <span className="w-5 h-5 rounded overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                            <img src={p.logo} alt={p.name} loading="lazy" className="w-full h-full object-contain p-[1px]" />
                          </span>
                        )}
                        <span className="text-white/85 pr-0.5">{p.name}</span>
                      </span>
                    );
                    return href ? (
                      <a
                        key={p.id}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`watch-provider-${p.id}`}
                      >
                        {inner}
                      </a>
                    ) : (
                      <span key={p.id} data-testid={`watch-provider-${p.id}`}>{inner}</span>
                    );
                  })}
                </div>
              )}

              {/* External link pills — IMDB, TMDB, Trakt */}
              {(details?.imdb_id || details?.id) && (
                <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="external-links-row">
                  {details?.imdb_id && (
                    <a
                      href={`https://www.imdb.com/title/${details.imdb_id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="external-link-imdb"
                      title="View on IMDB"
                      className="inline-flex items-center gap-1.5 bg-[#F5C518] hover:bg-[#FFD63A] text-black text-[11px] font-extrabold tracking-wider px-2.5 py-1 rounded-md transition-colors"
                    >
                      <span className="font-display">IMDb</span>
                      {details?.rating && <span className="opacity-80">{details.rating.toFixed(1)}</span>}
                    </a>
                  )}
                  {details?.id && (
                    <a
                      href={`https://www.themoviedb.org/${details.media_type || item.media_type}/${details.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="external-link-tmdb"
                      title="View on TMDB"
                      className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#01b4e4] via-[#90cea1] to-[#0d253f] hover:brightness-110 text-white text-[11px] font-extrabold tracking-wider px-2.5 py-1 rounded-md transition-all"
                    >
                      <span className="font-display">TMDB</span>
                    </a>
                  )}
                  {details?.id && (
                    <a
                      href={`https://letterboxd.com/tmdb/${details.id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="external-link-letterboxd"
                      title="View on Letterboxd"
                      className="inline-flex items-center gap-1.5 bg-[#202830] hover:bg-[#2c3641] text-white text-[11px] font-extrabold tracking-wider px-2.5 py-1 rounded-md border border-[#445566] transition-colors"
                    >
                      <span className="font-display">
                        <span className="text-[#00e054]">●</span>
                        <span className="text-[#40bcf4] mx-0.5">●</span>
                        <span className="text-[#ff8000]">●</span>
                      </span>
                      <span>LETTERBOXD</span>
                    </a>
                  )}
                  {details?.imdb_id && (
                    <a
                      href={`https://trakt.tv/search/imdb?id=${details.imdb_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="external-link-trakt"
                      title="View on Trakt"
                      className="inline-flex items-center gap-1.5 bg-[#ED1C24] hover:bg-[#ff3942] text-white text-[11px] font-extrabold tracking-wider px-2.5 py-1 rounded-md transition-colors"
                    >
                      <span className="font-display">TRAKT</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="px-6 md:px-8 pt-4 pb-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {loading ? (
                  <div className="space-y-2 max-w-3xl" data-testid="detail-modal-overview-skeleton">
                    <div className="tt-skeleton h-3 rounded" />
                    <div className="tt-skeleton h-3 rounded w-[92%]" />
                    <div className="tt-skeleton h-3 rounded w-[78%]" />
                  </div>
                ) : (
                  <p className="text-sm text-[var(--tt-text-muted)] leading-relaxed max-w-3xl">
                    {details?.overview || item.overview || "No overview available."}
                  </p>
                )}
              </div>
              {details && (
                <div className="shrink-0">
                  <ListToggleButtons
                    item={{ ...details, id: details.id }}
                    mediaType={details.media_type}
                    size={18}
                  />
                </div>
              )}
            </div>
            {details?.cast?.length > 0 && (
              <div className="mt-4 flex gap-4 overflow-x-auto pb-2">
                {details.cast.map((c, i) => (
                  <div key={i} className="text-center shrink-0 w-20">
                    <div className="w-16 h-16 rounded-full mx-auto overflow-hidden bg-[#222]">
                      {c.profile && <img src={c.profile} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="text-[11px] mt-1 font-semibold truncate">{c.name}</div>
                    <div className="text-[10px] text-[var(--tt-text-faint)] truncate">{c.character}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Screenshots + inline trailer */}
            {(details?.screenshots?.length > 0 || details?.trailer_key) && (
              <div className="mt-6 grid md:grid-cols-[1.4fr_1fr] gap-4" data-testid="hero-media-row">
                {details?.trailer_key && (
                  <div data-testid="hero-trailer">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-primary)] mb-2">
                      Trailer
                    </div>
                    <div className="relative aspect-video w-full rounded-xl overflow-hidden border border-[var(--tt-border)] bg-black">
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${details.trailer_key}?modestbranding=1&rel=0`}
                        title="Trailer"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                        className="absolute inset-0 w-full h-full"
                      />
                    </div>
                  </div>
                )}
                {details?.screenshots?.length > 0 && (
                  <div data-testid="hero-screenshots">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-yellow)] mb-2">
                      Screenshots
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {details.screenshots.slice(0, 6).map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightboxIdx(i)}
                          data-testid={`hero-screenshot-${i}`}
                          className="block aspect-video rounded-md overflow-hidden border border-[var(--tt-border)] hover:border-[var(--tt-primary)]/60 transition-colors"
                        >
                          <img src={s} alt="" loading="lazy" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Torrents */}
          <div className="px-6 md:px-8 pb-2 flex items-center gap-3 flex-wrap">
            <h3 className="font-display text-xl font-bold flex items-center gap-2">
              <span className="text-[var(--tt-primary)]">⬇</span>
              Torrents
              {torrents !== null && (
                <span className="bg-[var(--tt-yellow)] text-black text-xs font-bold px-2 py-0.5 rounded">
                  {filtered.length}
                </span>
              )}
            </h3>
            <button
              onClick={refreshTorrents}
              data-testid="torrents-refresh-btn"
              className="ml-auto text-xs text-[var(--tt-text-muted)] hover:text-white flex items-center gap-1 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingTor ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          {!user ? (
            <div className="px-6 md:px-8 pb-8" data-testid="torrents-auth-gate">
              <div className="relative rounded-3xl border border-[var(--tt-primary)]/30 bg-gradient-to-br from-[rgba(255,42,109,0.08)] via-transparent to-[rgba(255,215,0,0.05)] backdrop-blur-md p-6 md:p-8 overflow-hidden">
                <div
                  aria-hidden="true"
                  className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-30 blur-3xl pointer-events-none"
                  style={{ background: "var(--tt-primary)" }}
                />
                <div className="relative flex items-start gap-4 flex-wrap">
                  <div className="w-12 h-12 rounded-2xl bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🔒</span>
                  </div>
                  <div className="flex-1 min-w-[250px]">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[var(--tt-primary)] mb-1">
                      Members only
                    </div>
                    <h4 className="font-display text-xl md:text-2xl font-extrabold tracking-tight mb-2">
                      Sign up to see torrents
                    </h4>
                    <p className="text-sm text-white/75 leading-relaxed max-w-xl">
                      Magnet links, quality / codec filters, real-time seeder counts and the comments section are reserved for free members.
                      30 seconds, no credit card.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link
                        to="/register"
                        data-testid="torrents-gate-signup"
                        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-full bg-[var(--tt-primary)] text-white hover:bg-[var(--tt-primary-strong)] transition-colors"
                      >
                        Create free account
                      </Link>
                      <Link
                        to="/login"
                        data-testid="torrents-gate-login"
                        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/30"
                      >
                        I already have one
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
          <>
          <div className="px-6 md:px-8 py-3 flex items-center gap-2 flex-wrap">
            {QUALITIES.map((q) => (
              <button
                key={q}
                data-testid={`detail-quality-filter-${q.toLowerCase()}`}
                onClick={() => setQuality(q)}
                className={`text-xs font-bold px-4 py-1.5 rounded-full transition-all
                  ${quality === q
                    ? "bg-[var(--tt-yellow)] text-black shadow-[0_0_18px_rgba(249,168,38,0.35)]"
                    : "bg-[var(--tt-surface-2)] text-white hover:bg-[#222]"}`}
              >
                {q}
              </button>
            ))}

            {details?.media_type === "tv" && (
              <>
                <select
                  data-testid="detail-season-filter"
                  value={season}
                  onChange={(e) => { setSeason(e.target.value); setEpisode(""); }}
                  className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full px-3 py-1.5 text-xs outline-none cursor-pointer hover:border-[var(--tt-primary)]"
                >
                  <option value="">All Seasons</option>
                  {details.seasons?.map((s) => (
                    <option key={s.season_number} value={s.season_number}>
                      Season {s.season_number}
                    </option>
                  ))}
                </select>
                <select
                  data-testid="detail-episode-filter"
                  value={episode}
                  onChange={(e) => setEpisode(e.target.value)}
                  disabled={!season}
                  className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full px-3 py-1.5 text-xs outline-none cursor-pointer hover:border-[var(--tt-primary)] disabled:opacity-40"
                >
                  <option value="">All Episodes</option>
                  {episodes.map((e) => (
                    <option key={e.episode_number} value={e.episode_number}>
                      Ep {e.episode_number}: {e.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={reset}
                  data-testid="detail-reset-btn"
                  className="bg-[var(--tt-surface-2)] hover:bg-[#222] text-xs font-semibold px-3 py-1.5 rounded-full border border-[var(--tt-border)] flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Reset
                </button>
              </>
            )}
          </div>

          {/* Source filter chip row */}
          {torrents && Object.keys(sourceCounts).length > 1 && (
            <div className="px-6 md:px-8 pb-1 flex items-center gap-2 flex-wrap" data-testid="source-filter-row">
              <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-faint)] font-semibold mr-1">Source</div>
              <button
                onClick={() => setSourceFilter("ALL")}
                data-testid="source-filter-all"
                className={`text-[11px] font-bold px-3 py-1 rounded-full transition-colors
                  ${sourceFilter === "ALL"
                    ? "bg-[var(--tt-primary)] text-white"
                    : "bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] hover:text-white"}`}
              >
                All ({torrents.length})
              </button>
              {Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]).map(([src, count]) => (
                <button
                  key={src}
                  onClick={() => setSourceFilter(src === sourceFilter ? "ALL" : src)}
                  data-testid={`source-filter-${src.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`inline-flex items-center gap-1.5 px-1 py-0.5 rounded transition-all
                    ${sourceFilter === src ? "ring-2 ring-[var(--tt-primary)] ring-offset-1 ring-offset-black" : "opacity-70 hover:opacity-100"}`}
                  title={`Show only ${src}`}
                >
                  <SourceBadge source={src} />
                  <span className="text-[10px] font-mono-tt text-[var(--tt-text-muted)]">{count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Language filter chip row */}
          {torrents && Object.keys(languageCounts).length > 1 && (
            <div className="px-6 md:px-8 pb-1 flex items-center gap-2 flex-wrap" data-testid="language-filter-row">
              <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-faint)] font-semibold mr-1">Lang</div>
              <button
                onClick={() => setLanguageFilter("ALL")}
                data-testid="language-filter-all"
                className={`text-[11px] font-bold px-3 py-1 rounded-full transition-colors
                  ${languageFilter === "ALL"
                    ? "bg-[var(--tt-primary)] text-white"
                    : "bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] hover:text-white"}`}
              >
                All ({torrents.length})
              </button>
              {Object.entries(languageCounts).sort((a, b) => b[1] - a[1]).map(([lang, count]) => (
                <button
                  key={lang}
                  onClick={() => setLanguageFilter(lang === languageFilter ? "ALL" : lang)}
                  data-testid={`language-filter-${lang.toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 px-1 py-0.5 rounded transition-all
                    ${languageFilter === lang ? "ring-2 ring-[var(--tt-primary)] ring-offset-1 ring-offset-black" : "opacity-70 hover:opacity-100"}`}
                  title={`Show only ${lang}`}
                >
                  <LanguageBadge language={lang} />
                  <span className="text-[10px] font-mono-tt text-[var(--tt-text-muted)]">{count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Codec / Language / Verified filters */}
          <div className="px-6 md:px-8 pb-3 flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-faint)] font-semibold mr-1">Codec</div>
            {CODECS.map((c) => (
              <button
                key={c}
                data-testid={`detail-codec-filter-${c.toLowerCase().replace(/\./g, "")}`}
                onClick={() => setCodecFilter(c)}
                className={`text-[11px] font-bold px-3 py-1 rounded-full transition-colors
                  ${codecFilter === c
                    ? "bg-[var(--tt-purple)] text-white"
                    : "bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] hover:text-white"}`}
              >
                {c}
              </button>
            ))}
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--tt-text-muted)] ml-3 cursor-pointer">
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
                data-testid="detail-verified-toggle"
                className="accent-[var(--tt-primary)]"
              />
              Verified (10+ seeders)
            </label>
            <button
              onClick={fetchSubtitles}
              data-testid="detail-subtitles-btn"
              className="ml-auto flex items-center gap-1.5 bg-[var(--tt-surface-2)] hover:bg-[#222] text-[11px] font-semibold px-3 py-1.5 rounded-full border border-[var(--tt-border)]"
            >
              {subsBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Subtitles className="w-3 h-3" />}
              Find Subtitles
            </button>
          </div>

          {subtitles && (
            <div data-testid="detail-subtitles-list" className="px-6 md:px-8 pb-3">
              {subtitles.error ? (
                <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
                  {subtitles.error}
                </div>
              ) : subtitles.results?.length === 0 ? (
                <div className="text-xs text-[var(--tt-text-muted)]">No subtitles found.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {subtitles.results.map((s, i) => (
                    <div key={s.id || i} className="text-xs bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-md p-2">
                      <div className="font-semibold truncate">{s.file_name || s.release || "Subtitle"}</div>
                      <div className="flex items-center gap-2 text-[10px] text-[var(--tt-text-muted)] mt-1">
                        <span className="uppercase">{s.language}</span>
                        <span>↓ {s.download_count ?? 0}</span>
                        {s.url && (
                          <a href={s.url} target="_blank" rel="noreferrer" className="ml-auto text-[var(--tt-primary)] hover:underline">View</a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="pb-2">
            {loadingTor ? (
              <div className="space-y-2" data-testid="detail-modal-torrents-skeleton">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-text-muted)] flex items-center gap-2 mb-3">
                  <Loader2 className="w-3 h-3 animate-spin text-[var(--tt-primary)]" />
                  Searching YTS · EZTV · Knaben · 1337x · TorrentGalaxy · ext.to&hellip;
                </div>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-black/20 border border-[var(--tt-border)] px-3 py-3 flex items-center gap-3"
                    style={{ animationDelay: `${i * 0.12}s` }}
                  >
                    {/* Quality badge placeholder */}
                    <div className="tt-skeleton w-12 h-5 rounded-md flex-shrink-0" />
                    {/* Title rows */}
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="tt-skeleton h-3 rounded" style={{ width: `${72 - i * 6}%` }} />
                      <div className="flex items-center gap-1.5">
                        <div className="tt-skeleton h-3 w-12 rounded-full" />
                        <div className="tt-skeleton h-3 w-14 rounded-full" />
                        <div className="tt-skeleton h-3 w-10 rounded-full" />
                      </div>
                    </div>
                    {/* Seeders / leechers stub */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="tt-skeleton h-4 w-8 rounded" />
                      <div className="tt-skeleton h-4 w-8 rounded" />
                    </div>
                    {/* Download/info button placeholders */}
                    <div className="tt-skeleton w-7 h-7 rounded-md flex-shrink-0" />
                    <div className="tt-skeleton w-7 h-7 rounded-md flex-shrink-0" />
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center text-[var(--tt-text-muted)] py-12 text-sm">
                {torrents && torrents.length > 0
                  ? `No torrents match these filters. Try ${verifiedOnly ? "unchecking Verified, " : ""}${codecFilter !== "ALL" ? "any codec, " : ""}${languageFilter !== "ALL" ? "any language, " : ""}or quality=ALL.`
                  : `No torrents found for this ${details?.media_type === "tv" ? "show" : "movie"}${season ? ` (S${season}${episode ? `E${episode}` : ""})` : ""}.`}
              </div>
            ) : details?.media_type === "tv" ? (
              <>
                <TorrentList
                  title="Full Series"
                  rows={filtered.filter((t) => classifyTvTorrent(t) === "series")}
                  badgeColor="var(--tt-primary)"
                  testId="torrents-group-series"
                  mediaType="tv"
                  onModChange={refreshTorrents}
                  tmdbId={details?.id}
                />
                <TorrentList
                  title="Season Packs"
                  rows={filtered.filter((t) => classifyTvTorrent(t) === "season")}
                  badgeColor="var(--tt-yellow)"
                  testId="torrents-group-season"
                  mediaType="tv"
                  onModChange={refreshTorrents}
                  tmdbId={details?.id}
                />
                <TorrentList
                  title="Episodes"
                  rows={filtered.filter((t) => classifyTvTorrent(t) === "episode")}
                  badgeColor="var(--tt-purple)"
                  testId="torrents-group-episodes"
                  mediaType="tv"
                  onModChange={refreshTorrents}
                  tmdbId={details?.id}
                />
              </>
            ) : (
              <>
                {details?.collection && collectionTorrents && collectionTorrents.length > 0 && (
                  <div className="mb-4 rounded-xl border border-[var(--tt-yellow)]/40 bg-gradient-to-br from-[var(--tt-yellow)]/10 via-transparent to-transparent p-4">
                    <button
                      type="button"
                      data-testid="collection-banner-open"
                      onClick={() => {
                        onClose?.();
                        navigate(`/collection/${details.collection.id}`);
                      }}
                      className="w-full flex items-center gap-3 mb-3 text-left group"
                    >
                      {details.collection.poster && (
                        <img
                          src={details.collection.poster}
                          alt={details.collection.name}
                          className="w-9 h-14 rounded object-cover border border-[var(--tt-border)] group-hover:border-[var(--tt-yellow)] transition-colors"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-yellow)]">
                          Collection Pack
                        </div>
                        <div className="font-display text-base md:text-lg font-extrabold truncate group-hover:text-[var(--tt-yellow)] transition-colors">
                          {details.collection.name}
                        </div>
                        <div className="text-[11px] text-[var(--tt-text-muted)] group-hover:text-[var(--tt-text)] transition-colors">
                          View all films in this collection →
                        </div>
                      </div>
                    </button>
                    <TorrentList
                      title=""
                      rows={collectionTorrents}
                      badgeColor="var(--tt-yellow)"
                      testId="torrents-group-collection"
                      mediaType="movie"
                      onModChange={refreshTorrents}
                    />
                  </div>
                )}
                <TorrentList
                  title="Available Torrents"
                  rows={filtered}
                  badgeColor="var(--tt-primary)"
                  testId="torrents-group-movie"
                  mediaType="movie"
                  onModChange={refreshTorrents}
                  tmdbId={details?.id}
                />
              </>
            )}
          </div>

          {details && (
            <CommentsChatTabs tmdbId={details.id} mediaType={details.media_type} />
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
